import { useEffect, useState } from "react";
import { api, BullSheetPage, Manifest } from "../api";
import NewsSheet, { Edition } from "./NewsSheet";
import Pager from "./Pager";

const PAGE = 25;

export default function BullSheet(_: { manifest: Manifest }) {
  const [ed, setEd] = useState<Edition | null>(null);
  const [latestId, setLatestId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [archive, setArchive] = useState<BullSheetPage | null>(null);
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  // The link previews as THIS issue (volume, number, headline) and opens it,
  // not whatever is latest when the link is tapped.
  const shareUrl = ed?.issue?.share_url ?? `${window.location.origin}/share`;
  const readingOld = ed?.issue != null && latestId != null && ed.issue.id !== latestId;

  useEffect(() => { void load(false); }, []);
  useEffect(() => {
    const t = setTimeout(() => { setQ(query.trim()); setOffset(0); }, 250);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => { void fetchArchive(); }, [q, offset]);

  async function fetchArchive() {
    const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset), ...(q ? { q } : {}) });
    try { setArchive(await api.get<BullSheetPage>(`/api/paper/editions?${qs}`)); }
    catch (e) { setErr((e as Error).message); }
  }

  async function load(refresh: boolean) {
    setBusy(true); setErr(null);
    try {
      const e = await api.get<Edition>(`/api/paper/edition${refresh ? "?refresh=true" : ""}`);
      setEd(e);
      if (e.issue) setLatestId(e.issue.id);
      void fetchArchive();
    }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  async function openIssue(id: number) {
    setErr(null);
    try { setEd(await api.get<Edition>(`/api/paper/editions/${id}`)); window.scrollTo({ top: 0 }); }
    catch (e) { setErr((e as Error).message); }
  }

  async function share(url = shareUrl) {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { window.prompt("Public read-only link — copy it:", url); }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-lg font-bold">The Bull Sheet</h1>
        <div className="flex items-center gap-2">
          <button className="btn" onClick={() => share()} title={`Share a public, read-only link: ${shareUrl}`}>
            {copied ? "Link copied ✓" : ed?.issue ? `Share ${ed.issue.label}` : "Share with a friend"}
          </button>
          <button className="btn" onClick={() => load(true)} disabled={busy}>
            {busy ? "Setting the press…" : "New edition"}
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        “Share with a friend” copies a public, read-only link ({shareUrl}) — today’s paper and an overview of the firm, nothing account-specific.
      </p>
      {readingOld && ed?.issue && (
        <div className="card text-sm flex items-center gap-2 flex-wrap">
          <span>Reading {ed.issue.label} from {ed.date}, not the latest.</span>
          <button className="btn ml-auto" onClick={() => load(false)}>Back to the latest</button>
        </div>
      )}
      {err && <div className="text-down text-sm">{err}</div>}
      <NewsSheet ed={ed} />

      <section className="card space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="label mr-auto">Archive</h2>
          <input className="input w-64" placeholder="Search headlines and regime" value={query}
            onChange={(e) => setQuery(e.target.value)} />
        </div>
        {archive && archive.items.length === 0 && (
          <div className="text-sm text-slate-500">{q ? "No issues match this search." : "No issues pressed yet."}</div>
        )}
        {archive && archive.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-edge">
                  <th className="py-1.5 pr-3 font-medium">Issue</th>
                  <th className="py-1.5 pr-3 font-medium">Pressed</th>
                  <th className="py-1.5 pr-3 font-medium">Lead</th>
                  <th className="py-1.5 pr-3 font-medium">Regime</th>
                  <th className="py-1.5 font-medium" />
                </tr>
              </thead>
              <tbody>
                {archive.items.map((r) => (
                  <tr key={r.id} className={`border-b border-edge/60 ${ed?.issue?.id === r.id ? "bg-slate-800/60" : ""}`}>
                    <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap">{r.label}</td>
                    <td className="py-1.5 pr-3 text-xs text-slate-500 whitespace-nowrap">
                      {r.date}{r.edition_time ? ` · ${r.edition_time}` : ""}
                    </td>
                    <td className="py-1.5 pr-3 font-semibold min-w-[14rem]">{r.headline || "(no lead)"}</td>
                    <td className="py-1.5 pr-3 text-xs text-slate-400 min-w-[10rem]">{r.regime}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      <button className="btn text-xs mr-1.5" onClick={() => openIssue(r.id)}>Open</button>
                      <button className="btn text-xs" onClick={() => share(r.share_url)}>Copy link</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {archive && <Pager offset={archive.offset} limit={archive.limit} total={archive.total} onOffset={setOffset} />}
      </section>
    </div>
  );
}
